import { Route, Routes } from '@angular/router';

/**
 * The Leads view registry. List, Board, and Analysis are routes, so the URL
 * completely identifies the rendered collection presentation.
 */
export interface LeadViewEntry {
  /** Stable id — used for tracking, badges, and the a11y label. */
  id: string;
  label: string;
  /** Material Icons ligature. */
  icon: string;
  /** Route path (leading slash optional) that must be registered. */
  route: string;
  /** Optional explanatory tooltip. */
  note?: string;
}

export const LEADS_VIEW_STORAGE_KEY = 'leads-view';

/** The supported ways to inspect, manage, and analyze leads. */
export const LEAD_VIEWS: LeadViewEntry[] = [
  { id: 'list',     label: 'Table',    icon: 'table_rows',  route: '/leads/table' },
  { id: 'board',    label: 'Board',    icon: 'view_kanban', route: '/leads/board' },
  { id: 'analysis', label: 'Analysis', icon: 'insights',    route: '/leads/analysis',
    note: 'Funnel, open pipeline, and the won/lost trend.' },
];

/** Route `data` fields the switcher reads. Same contract the Dashboard uses. */
interface GuardedRouteData {
  permission?: string;
  permissionAny?: string[];
}

/** Strip the leading slash so a path compares against `Route.path`. */
function normalize(path: string): string {
  return path.replace(/^\/+/, '');
}

/**
 * The route registered at `path`, or null. It accepts both nested resource
 * routes and the older flat route tables used by callers and tests.
 */
export function findRoute(routes: Routes, path: string): Route | null {
  const wanted = normalize(path);
  const direct = routes.find(route => route.path === wanted);
  if (direct) return direct;
  const segments = wanted.split('/').filter(Boolean);
  let level = routes;
  let match: Route | undefined;
  for (const segment of segments) {
    match = level.find(route => route.path === segment);
    if (!match) return null;
    level = match.children ?? [];
  }
  return match ?? null;
}

/**
 * An entry is offered only when its route exists AND the caller holds what the
 * route itself declared. The route stays the single place a requirement is
 * written down.
 */
export function entryAvailable(
  entry: LeadViewEntry,
  routes: Routes,
  hasPermission: (key: string) => boolean,
): boolean {
  const route = findRoute(routes, entry.route);
  if (!route) return false;
  const data = (route.data ?? {}) as GuardedRouteData;
  if (data.permission) return hasPermission(data.permission);
  if (data.permissionAny) return data.permissionAny.some(hasPermission);
  return true;
}

/** The entries a given caller may actually be offered, in registry order. */
export function availableViews(
  routes: Routes,
  hasPermission: (key: string) => boolean,
  entries: LeadViewEntry[] = LEAD_VIEWS,
): LeadViewEntry[] {
  return entries.filter(e => entryAvailable(e, routes, hasPermission));
}
